/**
 * Location tag subline helpers for repeatable-section list NAME columns (#999).
 */

export type RepeatableTableLayout = 'desktop' | 'compact';

/**
 * Compact-fold policy gate (#999 Option B).
 *
 * The `compact` layout is shared by native mobile AND narrow mobile web (every
 * `*ConfigurationList` picks `compact` unless it is desktop web — see
 * `resolveRecordTableLayout`). Both
 * of those narrow surfaces are too tight for discrete ROOM/STATUS columns, so they
 * share the folded presentation (location + STATUS pill stacked under NAME,
 * weighted flex, abbreviated headers). Only wide desktop web (`'desktop'`) opts
 * out and keeps its byte-for-byte column layout. Reuse this everywhere the
 * compact device tables diverge from desktop.
 */
export function isNativeCompactLayout(layout: RepeatableTableLayout): boolean {
  return layout === 'compact';
}

export function joinRepeatableNameSublineParts(
  parts: string[],
): string | undefined {
  const trimmed = parts.map(part => part.trim()).filter(Boolean);
  return trimmed.length > 0 ? trimmed.join(' · ') : undefined;
}

/** NAME subline — optional compact-only parts plus facility location tag. */
export function buildRepeatableNameSubline(
  layout: RepeatableTableLayout,
  options: {
    compactParts?: Array<string | undefined>;
    locationTagName?: string | null;
  },
): string | undefined {
  const parts: string[] = [];
  if (layout === 'compact' && options.compactParts) {
    for (const part of options.compactParts) {
      const trimmed = part?.trim();
      if (trimmed) {
        parts.push(trimmed);
      }
    }
  }
  const location = options.locationTagName?.trim();
  if (location) {
    parts.push(location);
  }
  return joinRepeatableNameSublineParts(parts);
}

export function repeatableNameColumnUsesDoubleLine(
  layout: RepeatableTableLayout,
  locationTagName?: string | null,
): boolean {
  if (layout === 'compact') {
    return true;
  }
  return Boolean(locationTagName?.trim());
}

export type LocationTagNameResolver = (
  locationTagId: number | null | undefined,
) => string | null;

/** Internal column key for the facility location tag column (#999 Story 3.4). */
export const LOCATION_TAG_TABLE_COLUMN_KEY = 'ROOM';

/** User-facing header for {@link LOCATION_TAG_TABLE_COLUMN_KEY}. */
export const LOCATION_TAG_TABLE_COLUMN_HEADER = 'LOCATION';

/** Unit existing/new status column for Devices browse (#999). */
export const UNIT_STATUS_TABLE_COLUMN_KEY = 'STATUS';

export type RepeatableLocationTagColumnOptions = {
  /** Hide the location column (Browse-by-location is already tag-filtered). */
  hideLocationColumn?: boolean;
  /**
   * Devices view: show EXISTING/NEW as a STATUS column (pills) instead of
   * EXISTING / NEW section headers under each equipment type.
   */
  showStatusColumn?: boolean;
};

export function filterRepeatableLocationTagTableColumns<T extends string>(
  columns: readonly T[],
  options?: RepeatableLocationTagColumnOptions,
  layout: RepeatableTableLayout = 'desktop',
): readonly T[] {
  let next = columns;
  // Drop the standalone ROOM (location) column when the caller hides it
  // (Devices view — the tag is the card title) OR on native compact step tables,
  // where the location folds into the NAME subline (#999 double-line) so
  // NAME/PLATFORM/KW keep their width alongside the overflow menu. Web (desktop +
  // mobile web) keeps ROOM as a discrete column.
  if (options?.hideLocationColumn || isNativeCompactLayout(layout)) {
    next = next.filter(
      column => column !== LOCATION_TAG_TABLE_COLUMN_KEY,
    ) as readonly T[];
  }
  if (options?.showStatusColumn && !isNativeCompactLayout(layout)) {
    // Devices view (web/desktop + mobile web): STATUS is always the last data
    // column. On native compact the pill renders folded under NAME instead, so
    // STATUS is intentionally NOT appended as a standalone column (#999 Option B).
    return [...next, UNIT_STATUS_TABLE_COLUMN_KEY] as readonly T[];
  }
  return next;
}

export function formatRepeatableLocationTagColumnHeader(
  column: string,
  label: string,
): string {
  return column === LOCATION_TAG_TABLE_COLUMN_KEY
    ? LOCATION_TAG_TABLE_COLUMN_HEADER
    : label;
}

/** Prefix before MapPin when compact subline combines other fields with a tag. */
export function locationTagLeadingPrefix(
  subline: string | undefined,
  locationTagName: string | null | undefined,
): string | undefined {
  const location = locationTagName?.trim();
  if (!location || !subline?.trim()) {
    return undefined;
  }
  if (subline.trim() === location) {
    return undefined;
  }
  const separator = ` · ${location}`;
  if (subline.endsWith(separator)) {
    return subline.slice(0, subline.length - location.length);
  }
  return undefined;
}
